"use client"

import { useEffect, useRef, useState } from "react"
import { Play } from "lucide-react"
import { cn } from "@/lib/utils"

async function jpegPosterFromRemoteVideo(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const v = document.createElement("video")
    v.crossOrigin = "anonymous"
    v.muted = true
    v.playsInline = true
    v.preload = "auto"

    let done = false
    const settle = (out: string | null) => {
      if (done) return
      done = true
      v.pause()
      v.removeAttribute("src")
      v.load()
      resolve(out)
    }

    const onSeeked = () => {
      try {
        const w = v.videoWidth
        const h = v.videoHeight
        if (!w || !h) {
          settle(null)
          return
        }
        const c = document.createElement("canvas")
        c.width = w
        c.height = h
        const ctx = c.getContext("2d")
        if (!ctx) {
          settle(null)
          return
        }
        ctx.drawImage(v, 0, 0, w, h)
        settle(c.toDataURL("image/jpeg", 0.82))
      } catch {
        settle(null)
      }
    }

    const onLoadedMetadata = () => {
      const d = Number.isFinite(v.duration) ? v.duration : 0
      const t =
        d > 0
          ? Math.min(0.5, Math.max(d * 0.08, 0.06))
          : 0.1
      v.currentTime = t
    }

    v.onerror = () => settle(null)
    v.addEventListener("seeked", onSeeked, { once: true })
    v.addEventListener("loadedmetadata", onLoadedMetadata, { once: true })
    v.src = src
    v.load()
  })
}

type Props = {
  src: string
  overlayClassName?: string
}

export function ListingVideoThumb({ src, overlayClassName }: Props) {
  const [jpegPoster, setJpegPoster] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    let cancelled = false
    setJpegPoster(null)
    jpegPosterFromRemoteVideo(src).then((jpeg) => {
      if (!cancelled && jpeg) setJpegPoster(jpeg)
    })
    return () => {
      cancelled = true
    }
  }, [src])

  useEffect(() => {
    if (jpegPoster) return
    const el = videoRef.current
    if (!el) return
    const nudgeFrame = () => {
      try {
        const d = Number.isFinite(el.duration) ? el.duration : 0
        el.currentTime = d > 0 ? Math.min(0.45, Math.max(d * 0.06, 0.05)) : 0.12
      } catch {}
    }
    el.addEventListener("loadedmetadata", nudgeFrame, { once: true })
    return () => el.removeEventListener("loadedmetadata", nudgeFrame)
  }, [jpegPoster, src])

  return (
    <>
      {jpegPoster ? (
        <img
          src={jpegPoster}
          alt=""
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          draggable={false}
        />
      ) : (
        <video
          ref={videoRef}
          src={src}
          className="absolute inset-0 h-full w-full object-cover bg-black"
          muted
          playsInline
          preload="auto"
          aria-hidden
        />
      )}
      <span
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25",
          overlayClassName,
        )}
      >
        <Play className="h-9 w-9 text-white drop-shadow-md opacity-95" aria-hidden strokeWidth={1.5} />
      </span>
    </>
  )
}
